import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  Pressable,
  Share,
  TextInput,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { PageLayout } from '@/components/ui/page-layout';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  checkSlugAvailability,
  fetchSlugSuggestion,
  slugUnavailableMessage,
  useMyPublicProfile,
  useUpdateMyPublicProfile,
  useUploadBrandImage,
} from '@/hooks/use-public-profile';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { BrandColorPicker } from '@/components/ui/brand-color-picker';
import { CoverPhotoEditor, PickedImage } from '@/components/profile/cover-photo-editor';
import { DiscoveryLocationCard } from '@/components/profile/discovery-location-card';
import { SlugAvailability } from '@/types/public-profile';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { useThemedHex } from '@/hooks/use-theme-colors';

const BRAND_COLORS = [
  '#FD5001',
  '#E11D48',
  '#7C3AED',
  '#2563EB',
  '#0891B2',
  '#059669',
  '#CA8A04',
  '#334155',
];

const PUBLIC_BASE_URL = 'https://controlapag.com.br';

/** Aceita #rgb, #rrggbb ou rgb()/rgba() e normaliza para #RRGGBB (mantém o truque de alpha `${cor}1A`). */
function normalizeColor(input: string): string | null {
  const v = input.trim().toLowerCase();
  if (!v) return null;

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/.exec(v);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) {
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    }
    return `#${h.toUpperCase()}`;
  }

  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/.exec(v);
  if (rgb) {
    const parts = [rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, parseInt(n, 10)));
    return `#${parts
      .map((n) => n.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()}`;
  }

  return null;
}

type SlugCheckState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'done'; result: SlugAvailability };

export function PublicProfileScreen() {
  const th = useThemedHex();
  const { addToast } = useToastStore();
  const { user } = useAuthStore();

  const { data: profile, isLoading } = useMyPublicProfile();
  const updateProfile = useUpdateMyPublicProfile();
  const publicPlan = usePlanFeature('PUBLIC_PROFILE');
  const uploadImage = useUploadBrandImage();

  const [slugDraft, setSlugDraft] = useState('');
  const [confirmSlugChangeOpen, setConfirmSlugChangeOpen] = useState(false);
  const [slugCheck, setSlugCheck] = useState<SlugCheckState>({ status: 'idle' });
  const [category, setCategory] = useState('');
  const [businessHours, setBusinessHours] = useState('');
  const [instagram, setInstagram] = useState('');
  const [website, setWebsite] = useState('');
  const [customColor, setCustomColor] = useState('');
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hydratedRef = useRef(false);
  const [coverEditor, setCoverEditor] = useState<PickedImage | null>(null);

  // Preenche o formulário quando o perfil carrega
  useEffect(() => {
    if (!profile || hydratedRef.current) return;
    hydratedRef.current = true;
    setSlugDraft(profile.slug ?? '');
    setCategory(profile.category ?? '');
    setBusinessHours(profile.businessHours ?? '');
    setInstagram(profile.socialLinks?.instagram ?? '');
    setWebsite(profile.socialLinks?.website ?? '');

    if (!profile.slug) {
      void fetchSlugSuggestion()
        .then((slug) => setSlugDraft((current) => current || slug))
        .catch(() => undefined);
    }
  }, [profile]);

  const handleSlugChange = (value: string) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlugDraft(normalized);
    setSlugCheck({ status: 'idle' });

    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    if (!normalized || normalized === profile?.slug) return;

    setSlugCheck({ status: 'checking' });
    slugCheckTimer.current = setTimeout(() => {
      checkSlugAvailability(normalized)
        .then((result) => setSlugCheck({ status: 'done', result }))
        .catch(() => setSlugCheck({ status: 'idle' }));
    }, 500);
  };

  const saveSlug = () => {
    // Salvar o link já publica: link guardado e oculto não serve para nada.
    // No gratuito só guarda — publicar é recurso pago.
    const publish = !profile?.isPublic && !publicPlan.locked;
    updateProfile.mutate(
      publish ? { slug: slugDraft, isPublic: true } : { slug: slugDraft },
      {
        onSuccess: () =>
          addToast(
            publish ? 'Perfil publicado! Compartilhe seu link.' : 'Link do perfil atualizado!',
            'success',
          ),
        onError: (error: any) =>
          addToast(getApiErrorMessage(error, 'Não foi possível salvar o link.'), 'error'),
      },
    );
  };

  const handleSaveSlug = () => {
    if (!slugDraft || slugDraft === profile?.slug) return;
    // Alterar slug existente quebra o link antigo — avisa antes
    if (profile?.slug) {
      setConfirmSlugChangeOpen(true);
      return;
    }
    saveSlug();
  };

  const handleTogglePublic = (value: boolean) => {
    if (value && !publicPlan.guard()) return;
    if (value && !profile?.slug) {
      addToast('Salve o link do seu perfil antes de publicar.', 'error');
      return;
    }
    updateProfile.mutate(
      { isPublic: value },
      {
        onSuccess: () =>
          addToast(
            value ? 'Perfil publicado! Compartilhe seu link.' : 'Perfil despublicado.',
            'success',
          ),
        onError: (error: any) =>
          addToast(getApiErrorMessage(error, 'Erro ao atualizar o perfil.'), 'error'),
      },
    );
  };

  const handleToggleSection = (
    key: 'showGallery' | 'showServices' | 'showInfo',
    value: boolean,
  ) => {
    updateProfile.mutate(
      { [key]: value },
      {
        onError: () => addToast('Erro ao atualizar seção.', 'error'),
      },
    );
  };

  const handleSaveDetails = () => {
    updateProfile.mutate(
      {
        category: category.trim() || null,
        businessHours: businessHours.trim() || null,
        socialLinks:
          instagram.trim() || website.trim()
            ? { instagram: instagram.trim() || undefined, website: website.trim() || undefined }
            : null,
      },
      {
        onSuccess: () => addToast('Informações salvas!', 'success'),
        onError: (error: any) =>
          addToast(getApiErrorMessage(error, 'Erro ao salvar informações.'), 'error'),
      },
    );
  };

  const pickBrandImage = async (kind: 'logo' | 'cover') => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para acessar galeria é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      // A capa é enquadrada no editor próprio; o logo usa o crop nativo (1:1).
      // allowsEditing quebra o picker na web (retorna cancelado) — só no nativo.
      allowsEditing: kind === 'logo' && Platform.OS !== 'web',
      aspect: kind === 'logo' ? [1, 1] : undefined,
      quality: kind === 'logo' ? 0.8 : 1,
    });

    if (result.canceled || !result.assets[0]) return;
    const asset = result.assets[0];

    if (kind === 'cover') {
      setCoverEditor({
        uri: asset.uri,
        width: asset.width ?? 0,
        height: asset.height ?? 0,
      });
      return;
    }

    uploadImage.mutate(
      { kind, imageUri: asset.uri },
      {
        onSuccess: () => addToast('Logo atualizado!', 'success'),
        onError: (error: any) => addToast(getApiErrorMessage(error, 'Erro ao enviar imagem.'), 'error'),
      },
    );
  };

  const handleCoverConfirm = (croppedUri: string) => {
    uploadImage.mutate(
      { kind: 'cover', imageUri: croppedUri },
      {
        onSuccess: () => {
          addToast('Capa atualizada!', 'success');
          setCoverEditor(null);
        },
        onError: (error: any) => addToast(getApiErrorMessage(error, 'Erro ao enviar imagem.'), 'error'),
      },
    );
  };

  const publicUrl = profile?.slug ? `${PUBLIC_BASE_URL}/@${profile.slug}` : null;

  const handleShare = async () => {
    if (!publicUrl) return;
    try {
      if (Platform.OS === 'web') {
        if (typeof navigator !== 'undefined' && navigator.share) {
          await navigator.share({ url: publicUrl });
        } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
          await navigator.clipboard.writeText(publicUrl);
          addToast('Link copiado!', 'success');
        }
        return;
      }
      await Share.share({ message: `${publicUrl}?utm_source=profile_share` });
    } catch {
      // usuário cancelou o share sheet
    }
  };

  const handlePreview = () => {
    if (!profile?.slug) return;
    if (!profile.isPublic) {
      addToast('Publique o perfil para ver como cliente.', 'error');
      return;
    }
    if (Platform.OS === 'web') {
      void Linking.openURL(`/@${profile.slug}`);
      return;
    }
    router.push(`/@${profile.slug}` as never);
  };

  const slugFeedback = (() => {
    if (!slugDraft || slugDraft === profile?.slug) return null;
    if (slugCheck.status === 'checking') {
      return <ThemedText className="text-foreground/50 text-xs">Verificando...</ThemedText>;
    }
    if (slugCheck.status === 'done') {
      return slugCheck.result.available ? (
        <ThemedText className="text-xs text-green-600">Link disponível!</ThemedText>
      ) : (
        <ThemedText className="text-xs text-error-500">
          {slugUnavailableMessage(slugCheck.result.reason)}
        </ThemedText>
      );
    }
    return null;
  })();

  if (isLoading || !profile) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <PageLayout title="Perfil público" subtitle="Sua vitrine para atrair novos clientes">
      <View className="gap-4">
        {/* Publicar + link */}
        <Card>
          <CardContent className="gap-3 py-4">
            {/* Switch em coluna, abaixo do título: na linha ao lado do texto ele sumia no nativo. */}
            <View className="items-start gap-2">
              <ThemedText className="font-semibold">Perfil público ativo</ThemedText>
              <Switch checked={profile.isPublic} onCheckedChange={handleTogglePublic} />
              <ThemedText className="text-foreground/60 text-sm">
                {publicPlan.locked
                  ? profile.isPublic
                    ? 'No plano gratuito sua vitrine fica fora do ar. Ela volta sozinha quando você assinar.'
                    : 'Publicar faz parte dos planos pagos. Você já pode deixar tudo pronto.'
                  : profile.isPublic
                    ? 'Qualquer pessoa com o link pode ver sua vitrine.'
                    : 'Seu perfil está oculto. Nada é exposto na web.'}
              </ThemedText>
            </View>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Link do perfil</ThemedText>
              <View className="flex-row items-center gap-2">
                {/* Sem o protocolo: com ele, o prefixo come metade da linha no celular. */}
                <ThemedText className="text-foreground/50 text-sm">
                  {PUBLIC_BASE_URL.replace(/^https?:\/\//, '')}/@
                </ThemedText>
                <TextInput
                  value={slugDraft}
                  onChangeText={handleSlugChange}
                  autoCapitalize="none"
                  autoCorrect={false}
                  maxLength={30}
                  placeholder="seunegocio"
                  placeholderTextColor="#999"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                />
              </View>
              {slugFeedback}
              {slugDraft !== (profile.slug ?? '') && slugDraft.length > 0 && (
                <Button
                  size="sm"
                  title={
                    updateProfile.isPending
                      ? 'Salvando...'
                      : profile.isPublic || publicPlan.locked
                        ? 'Salvar link'
                        : 'Salvar e publicar'
                  }
                  disabled={
                    updateProfile.isPending ||
                    (slugCheck.status === 'done' && !slugCheck.result.available)
                  }
                  onPress={handleSaveSlug}
                />
              )}
            </View>

            {publicUrl && profile.isPublic && (
              <View className="flex-row flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="grow basis-40"
                  title="Compartilhar"
                  onPress={() => void handleShare()}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="grow basis-40"
                  title="Ver como cliente"
                  onPress={handlePreview}
                />
              </View>
            )}
          </CardContent>
        </Card>

        {/* Kit de divulgação */}
        {profile.isPublic && profile.slug && (
          <Card>
            <CardContent className="gap-3 pt-4 pb-6">
              <ThemedText className="font-semibold">Divulgação</ThemedText>
              <ThemedText className="text-foreground/60 text-sm">
                Ferramentas para atrair clientes com a sua marca.
              </ThemedText>
              <Button
                variant="outline"
                title="QR code do perfil"
                onPress={() => router.push('/(tabs)/(provider)/profile/qr-code')}
              />
              <Button
                variant="outline"
                title="Criar post para redes sociais"
                onPress={() => router.push('/(tabs)/(provider)/profile/post-generator')}
              />
            </CardContent>
          </Card>
        )}

        {/* Marca: logo, capa e cor — a tela é do dono da vitrine. */}
        <Card>
          <CardContent className="gap-4 py-4">
            <ThemedText className="font-semibold">Identidade da marca</ThemedText>

            <View className="flex-row gap-4">
              <Pressable onPress={() => void pickBrandImage('logo')} className="items-center gap-1">
                {profile.logoUrl || user?.image ? (
                  <Image
                    source={{ uri: profile.logoUrl ?? user?.image }}
                    aria-hidden
                    style={{ width: 72, height: 72, borderRadius: 36 }}
                  />
                ) : (
                  <View className="h-[72px] w-[72px] items-center justify-center rounded-full border border-dashed border-border">
                    <Ionicons className="text-neutral-400" name="image-outline" size={22} />
                  </View>
                )}
                <ThemedText className="text-foreground/60 text-xs">Logo</ThemedText>
              </Pressable>

              <Pressable
                onPress={() => void pickBrandImage('cover')}
                className="flex-1 items-center gap-1"
              >
                {profile.coverUrl ? (
                  <Image
                    source={{ uri: profile.coverUrl }}
                    aria-hidden
                    style={{ width: '100%', height: 72, borderRadius: 8 }}
                    resizeMode="cover"
                  />
                ) : (
                  <View className="h-[72px] w-full items-center justify-center rounded-lg border border-dashed border-border">
                    <Ionicons className="text-neutral-400" name="images-outline" size={22} />
                  </View>
                )}
                <ThemedText className="text-foreground/60 text-xs">Foto de capa</ThemedText>
              </Pressable>
            </View>
            {uploadImage.isPending && (
              <ThemedText className="text-foreground/50 text-xs">Enviando imagem...</ThemedText>
            )}

            <View className="gap-3">
              <ThemedText className="font-medium text-sm">Cor da marca</ThemedText>
              <View className="flex-row flex-wrap gap-3">
                {(profile.brandColor && !BRAND_COLORS.includes(profile.brandColor.toUpperCase())
                  ? [...BRAND_COLORS, profile.brandColor.toUpperCase()]
                  : BRAND_COLORS
                ).map((color) => {
                  const selected = profile.brandColor?.toUpperCase() === color;
                  return (
                    <Pressable
                      key={color}
                      onPress={() => updateProfile.mutate({ brandColor: color })}
                      accessibilityLabel={`Cor ${color}`}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 18,
                        backgroundColor: color,
                        borderWidth: selected ? 3 : 0,
                        borderColor: th('#FFFFFF'),
                        elevation: selected ? 3 : 0,
                      }}
                    >
                      {selected && (
                        <View className="flex-1 items-center justify-center">
                          <Ionicons name="checkmark" size={18} color="#fff" />
                        </View>
                      )}
                    </Pressable>
                  );
                })}
              </View>

              <View className="gap-1.5">
                <ThemedText className="text-foreground/60 text-xs">
                  Cor personalizada (hex ou rgb)
                </ThemedText>
                <View className="flex-row items-center gap-2">
                  <BrandColorPicker
                    value={
                      normalizeColor(customColor) ?? profile.brandColor?.toUpperCase() ?? '#FD5001'
                    }
                    onSelect={(hex) => setCustomColor(hex)}
                  />
                  <TextInput
                    value={customColor}
                    onChangeText={setCustomColor}
                    placeholder="#FD5001 ou rgb(253,80,1)"
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    autoCorrect={false}
                    maxLength={25}
                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                  />
                  <Button
                    size="sm"
                    title="Usar"
                    disabled={!normalizeColor(customColor)}
                    onPress={() => {
                      const hex = normalizeColor(customColor);
                      if (!hex) return;
                      updateProfile.mutate({ brandColor: hex });
                      setCustomColor('');
                    }}
                  />
                </View>
              </View>
            </View>
          </CardContent>
        </Card>

        {/* Informações práticas */}
        <Card>
          <CardContent className="gap-3 py-4">
            <ThemedText className="font-semibold">Informações do negócio</ThemedText>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Categoria</ThemedText>
              <TextInput
                value={category}
                onChangeText={setCategory}
                placeholder="Ex.: Personal trainer, Diarista, Professor..."
                placeholderTextColor="#999"
                maxLength={60}
                className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </View>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Horário de atendimento</ThemedText>
              <TextInput
                value={businessHours}
                onChangeText={setBusinessHours}
                placeholder="Ex.: Seg a Sex, 8h às 18h"
                placeholderTextColor="#999"
                maxLength={280}
                className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </View>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Instagram</ThemedText>
              <TextInput
                value={instagram}
                onChangeText={setInstagram}
                placeholder="@seunegocio"
                placeholderTextColor="#999"
                autoCapitalize="none"
                maxLength={100}
                className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </View>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Site</ThemedText>
              <TextInput
                value={website}
                onChangeText={setWebsite}
                placeholder="https://..."
                placeholderTextColor="#999"
                autoCapitalize="none"
                maxLength={200}
                className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </View>

            <Button
              title={updateProfile.isPending ? 'Salvando...' : 'Salvar informações'}
              disabled={updateProfile.isPending}
              onPress={handleSaveDetails}
            />
          </CardContent>
        </Card>

        {/* Região e categoria: é o que faz o prestador ser achado na busca */}
        <DiscoveryLocationCard profile={profile} />

        {/* Seções da página */}
        <Card>
          <CardContent className="gap-3 py-4">
            <ThemedText className="font-semibold">Seções da página</ThemedText>
            <ThemedText className="text-foreground/60 text-sm">
              Monte sua vitrine: seções desativadas não aparecem para o cliente.
            </ThemedText>

            {[
              {
                key: 'showGallery' as const,
                label: 'Galeria de trabalhos',
                value: profile.showGallery,
              },
              {
                key: 'showServices' as const,
                label: 'Serviços',
                value: profile.showServices,
              },
              {
                key: 'showInfo' as const,
                label: 'Informações práticas',
                value: profile.showInfo,
              },
            ].map((section) => (
              <View key={section.key} className="flex-row items-center justify-between">
                <ThemedText>{section.label}</ThemedText>
                <Switch
                  checked={section.value}
                  onCheckedChange={(value) => handleToggleSection(section.key, value)}
                />
              </View>
            ))}

            <Button
              variant="outline"
              title="Gerenciar fotos da galeria"
              onPress={() => router.push('/(tabs)/(provider)/profile/gallery')}
            />
          </CardContent>
        </Card>
      </View>

      <ConfirmDialog
        open={confirmSlugChangeOpen}
        onOpenChange={setConfirmSlugChangeOpen}
        title="Alterar link do perfil"
        message={`O link antigo (${PUBLIC_BASE_URL}/@${profile?.slug}) vai deixar de funcionar. Continuar?`}
        confirmLabel="Alterar"
        destructive
        onConfirm={saveSlug}
      />

      <CoverPhotoEditor
        open={!!coverEditor}
        image={coverEditor}
        uploading={uploadImage.isPending}
        onCancel={() => setCoverEditor(null)}
        onConfirm={handleCoverConfirm}
      />
    </PageLayout>
  );
}
